/**
 * Persistence for an in-flight Plaid Link session across the OAuth browser
 * round-trip (ADR-070).
 *
 * OAuth institutions (Chase, Bank of America, Wells Fargo, Capital One — most large
 * US banks) hand the treasurer off to the bank's OWN login page and then redirect
 * back to the finance setup page. That redirect is a full page navigation, so every
 * bit of React state — including the active `link_token` — is wiped. To let Plaid
 * Link resume where it left off we must survive that navigation: stash the active
 * `link_token` (and which flow opened it) before Link opens, then restore it and
 * re-open Link with `receivedRedirectUri` set when the page reloads carrying Plaid's
 * `oauth_state_id` marker.
 *
 * The stored session is cleared on Link success / exit so a later attempt never
 * resumes a dead session. A credential-based (non-OAuth) link never navigates away,
 * so it saves-then-clears within one page load and is otherwise unaffected.
 */

const STORAGE_KEY = 'signalvote.plaid.linkSession';

/**
 * Which link flow opened Plaid Link. Drives the correct continuation on resume — a
 * first-time link and a re-link exchange the public token, whereas a reconnect
 * (update mode) just confirms — and guarantees only the flow that started the
 * session picks it back up (no cross-flow resume).
 */
export type PlaidLinkFlow = 'link' | 'relink' | 'reconnect';

export interface PlaidLinkSession {
  /** The active Plaid `link_token`; the SAME token must drive the OAuth resume. */
  token: string;
  flow: PlaidLinkFlow;
  /** The connection being re-linked / reconnected (absent for a first-time link). */
  connectionId?: string;
}

export interface PlaidLinkResume {
  token: string;
  connectionId?: string;
  /** The current URL, handed to the SDK so it can read `oauth_state_id` and continue. */
  receivedRedirectUri: string;
}

/**
 * Persist the active session before Plaid Link opens. Storage-disabled contexts
 * (private mode) fail silently: OAuth resume just won't work, the non-OAuth happy
 * path is unaffected.
 */
export function savePlaidLinkSession(session: PlaidLinkSession): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // no-op — see doc comment
  }
}

/** Read the persisted session, or null if none / corrupt / missing its token. */
export function loadPlaidLinkSession(): PlaidLinkSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PlaidLinkSession;
    if (!parsed || typeof parsed.token !== 'string' || parsed.token.length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Drop the persisted session so a later link attempt never resumes a dead one. */
export function clearPlaidLinkSession(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // no-op
  }
}

/**
 * True when the page loaded carrying Plaid's OAuth return marker — the
 * `oauth_state_id` query param Plaid appends to the registered redirect URI. Its
 * presence is how we know to resume Link rather than start fresh.
 */
export function isPlaidOAuthReturn(): boolean {
  try {
    return new URLSearchParams(window.location.search).has('oauth_state_id');
  } catch {
    return false;
  }
}

/**
 * If the page loaded from an OAuth return AND a session for `flow` was stashed,
 * return everything needed to resume Link — the same `link_token`, the connection
 * id (for relink/reconnect), and the current URL as `receivedRedirectUri`.
 * Otherwise null. The `flow` guard means each flow only ever resumes its own
 * session — a first-time link never picks up a reconnect's redirect, and vice versa.
 */
export function resumePlaidLink(flow: PlaidLinkFlow): PlaidLinkResume | null {
  if (!isPlaidOAuthReturn()) return null;
  const session = loadPlaidLinkSession();
  if (!session || session.flow !== flow) return null;
  return {
    token: session.token,
    connectionId: session.connectionId,
    receivedRedirectUri: window.location.href,
  };
}
